import { useCallback, useEffect, useState } from 'react';
import { format, subDays } from 'date-fns';
import {
  type AllDetailsFiltersState,
  type AllDetailsSortState,
  type AttributeFilter,
  type DashboardBaseFilters,
  type DashboardBlockId,
  type DashboardQualityDisplaySettings,
  type HiddenCriterionRef,
} from '@/entities/dashboards';
import { useSettings } from '@/entities/settings';
import { DEFAULT_ZONE_RANGES } from '@/shared/lib';
import { migrateLegacyAttributeFilters } from '@/features/attribute-filters';
import { type DashboardFiltersState } from '../ui/QualityFilters';
import { normalizeHiddenCriteria } from './hiddenCriteria';

/**
 * Период дашборда по умолчанию — последние 30 дней. Без дефолтного периода
 * первое открытие грузит всю историю оценок, и агрегация на бэке занимает
 * секунды; ограничение периодом делает первый отклик быстрым.
 */
const DEFAULT_PERIOD_DAYS = 30;
const getDefaultDateFrom = (): string => format(subDays(new Date(), DEFAULT_PERIOD_DAYS), 'yyyy-MM-dd');
const getDefaultDateTo = (): string => format(new Date(), 'yyyy-MM-dd');

/**
 * Собирает состояние базовых фильтров панели из сохранённых настроек пользователя,
 * дополняя отсутствующие поля значениями по умолчанию (пустой фильтр, период —
 * последние 30 дней, режим тепловой карты — по месяцам). Пустая строка в
 * `dateFrom`/`dateTo` сохраняется как есть — это снятая пользователем граница,
 * которую не нужно подменять периодом по умолчанию.
 */
const resolveBaseFilters = (stored: DashboardBaseFilters | undefined): DashboardFiltersState => ({
  consultant: stored?.consultant ?? '',
  dateFrom: stored?.dateFrom ?? getDefaultDateFrom(),
  dateTo: stored?.dateTo ?? getDefaultDateTo(),
  heatmapMode: stored?.heatmapMode ?? 'month',
});

/**
 * Полностью определённое состояние настроек отображения дашборда: пороги зон, скрытые
 * критерии и скрытые блоки — с подставленными значениями по умолчанию.
 */
export type ResolvedDisplaySettings = {
  zoneRanges: NonNullable<DashboardQualityDisplaySettings['zoneRanges']>;
  hiddenCriteria: HiddenCriterionRef[];
  hiddenBlocks: DashboardBlockId[];
};

/**
 * Собирает состояние настроек отображения из сохранённых настроек пользователя, подставляя
 * пороги зон по умолчанию и пустые наборы скрытых критериев/блоков при их отсутствии.
 * Скрытые критерии дополнительно приводятся к текущему формату (см. `normalizeHiddenCriteria`).
 */
const resolveDisplaySettings = (stored: DashboardQualityDisplaySettings | undefined): ResolvedDisplaySettings => ({
  zoneRanges: stored?.zoneRanges ?? DEFAULT_ZONE_RANGES,
  hiddenCriteria: normalizeHiddenCriteria(stored?.hiddenCriteria),
  hiddenBlocks: stored?.hiddenBlocks ?? [],
});

/**
 * Персистируемое за пользователем состояние дашборда качества и обработчики его изменения.
 *
 * Все настройки фильтрации (базовые фильтры панели, атрибутные фильтры, сортировка и
 * колоночные фильтры таблицы «Все детали») и настройки отображения (границы зон, скрытые
 * критерии и блоки) читаются из `uiSettings.dashboardQuality` при инициализации и
 * синхронизируются обратно в стор настроек при изменении — так они восстанавливаются при
 * следующем открытии дашборда. Эффекты синхронизируют локальный стейт, если настройки
 * обновились извне (например, после загрузки профиля при первом входе).
 */
export const useDashboardSettings = () => {
  const { dashboardQuality, setDashboardQualitySettings } = useSettings();

  // Все базовые фильтры дашборда — единым объектом (одно состояние вместо
  // отдельных useState на каждое поле). Поля «Дата от»/«Дата до» — в формате
  // YYYY-MM-DD; пустая строка — граница не задана и в запрос не уходит. При
  // инициализации читаются из uiSettings; изменения синхронизируются обратно
  // в стор настроек.
  const [filters, setFilters] = useState<DashboardFiltersState>(() => resolveBaseFilters(dashboardQuality.baseFilters));

  // Сортировка и фильтры таблицы «Все детали» — тоже персистятся за пользователем:
  // инициализируются из uiSettings и синхронизируются обратно при изменении.
  const [detailsSort, setDetailsSort] = useState<AllDetailsSortState | null>(
    () => dashboardQuality.detailsSort ?? null,
  );
  const [detailsFilters, setDetailsFilters] = useState<AllDetailsFiltersState>(
    () => dashboardQuality.detailsFilters ?? {},
  );

  // Настройки отображения (границы зон, скрытые критерии, скрытые блоки) — тоже персистятся
  // за пользователем, но, в отличие от фильтров, не сбрасываются кнопкой «Сбросить фильтры».
  const [display, setDisplay] = useState<ResolvedDisplaySettings>(() =>
    resolveDisplaySettings(dashboardQuality.display),
  );

  // Локальный стейт активных атрибутных фильтров; при инициализации читается
  // из uiSettings, изменения отправляются в стор настроек (а оттуда — на бэк
  // через useUiSettingsSync с дебаунсом 500 мс). Сохранённые в старом формате
  // (с ключом `code`) фильтры мигрируются на лету в формат с ключом `path`.
  const [activeAttributeFilters, setActiveAttributeFilters] = useState<AttributeFilter[]>(() =>
    migrateLegacyAttributeFilters(dashboardQuality.attributeFilters),
  );

  const handleChangeAttributeFilters = useCallback(
    (next: AttributeFilter[]) => {
      setActiveAttributeFilters(next);
      setDashboardQualitySettings({ attributeFilters: next });
    },
    [setDashboardQualitySettings],
  );

  // Базовые фильтры панели: локально обновляем и синхронизируем в uiSettings,
  // чтобы период, консультант и режим тепловой карты восстанавливались при
  // следующем открытии дашборда.
  const handleChangeFilters = useCallback(
    (next: DashboardFiltersState) => {
      setFilters(next);
      setDashboardQualitySettings({ baseFilters: next });
    },
    [setDashboardQualitySettings],
  );

  // Сортировка таблицы «Все детали»: локально + в uiSettings.
  const handleChangeDetailsSort = useCallback(
    (next: AllDetailsSortState | null) => {
      setDetailsSort(next);
      setDashboardQualitySettings({ detailsSort: next });
    },
    [setDashboardQualitySettings],
  );

  // Колоночные фильтры таблицы «Все детали»: локально + в uiSettings.
  const handleChangeDetailsFilters = useCallback(
    (next: AllDetailsFiltersState) => {
      setDetailsFilters(next);
      setDashboardQualitySettings({ detailsFilters: next });
    },
    [setDashboardQualitySettings],
  );

  // Настройки отображения: локально + в uiSettings. Приходит полный объект настроек, поэтому
  // сохраняем его целиком (стор мержит `display` как единое поле).
  const handleChangeDisplay = useCallback(
    (next: DashboardQualityDisplaySettings) => {
      const resolved = resolveDisplaySettings(next);
      setDisplay(resolved);
      setDashboardQualitySettings({ display: resolved });
    },
    [setDashboardQualitySettings],
  );

  // Синхронизация локального стейта с настройками, если они обновились извне
  // (например, после загрузки профиля при первом входе).
  // Сохранённые в старом формате (с ключом `code`) фильтры мигрируются на лету и
  // сразу записываются обратно в uiSettings — это переводит сохранённое состояние
  // на новый контракт без действий пользователя.
  useEffect(() => {
    const source = dashboardQuality.attributeFilters;
    const migrated = migrateLegacyAttributeFilters(source);
    setActiveAttributeFilters(migrated);
    const hasLegacy = (source ?? []).some((f) => !('path' in f) || !(f as { path?: string }).path);
    if (hasLegacy) {
      setDashboardQualitySettings({ attributeFilters: migrated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardQuality.attributeFilters]);

  // Синхронизация базовых фильтров, сортировки и колоночных фильтров таблицы
  // с настройками, если они обновились извне (загрузка профиля при первом входе).
  useEffect(() => {
    setFilters(resolveBaseFilters(dashboardQuality.baseFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardQuality.baseFilters]);

  useEffect(() => {
    setDetailsSort(dashboardQuality.detailsSort ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardQuality.detailsSort]);

  useEffect(() => {
    setDetailsFilters(dashboardQuality.detailsFilters ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardQuality.detailsFilters]);

  useEffect(() => {
    setDisplay(resolveDisplaySettings(dashboardQuality.display));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardQuality.display]);

  const handleResetFilters = useCallback(() => {
    // Сброс очищает базовые фильтры и возвращает период к умолчанию (последние
    // 30 дней), сохраняя режим тепловой карты. Атрибутные фильтры, сортировка и
    // колоночные фильтры таблицы «Все детали» тоже сбрасываются. Все сброшенные
    // значения синхронизируются в uiSettings — чтобы сброс пережил перезагрузку.
    const nextFilters: DashboardFiltersState = {
      consultant: '',
      dateFrom: getDefaultDateFrom(),
      dateTo: getDefaultDateTo(),
      heatmapMode: filters.heatmapMode,
    };
    setFilters(nextFilters);
    setActiveAttributeFilters([]);
    setDetailsSort(null);
    setDetailsFilters({});
    setDashboardQualitySettings({
      baseFilters: nextFilters,
      attributeFilters: [],
      detailsSort: null,
      detailsFilters: {},
    });
  }, [filters.heatmapMode, setDashboardQualitySettings]);

  const handleChangeAllDetailsColumns = useCallback(
    (next: { visibleColumns: string[]; columnOrder: string[] }) => {
      setDashboardQualitySettings({ allDetailsColumns: next });
    },
    [setDashboardQualitySettings],
  );

  return {
    filters,
    detailsSort,
    detailsFilters,
    display,
    activeAttributeFilters,
    allDetailsColumns: dashboardQuality.allDetailsColumns,
    handleChangeFilters,
    handleChangeAttributeFilters,
    handleChangeDetailsSort,
    handleChangeDetailsFilters,
    handleChangeDisplay,
    handleResetFilters,
    handleChangeAllDetailsColumns,
  };
};
